import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import FeedbackDrawer from './components/feedback/FeedbackDrawer';
import { useState, useEffect, createContext, useContext } from 'react';
import { debugCollector } from './utils/DebugCollector';
import LoginPage from './pages/Login';
import TwoFactorVerifyPage from './pages/TwoFactorVerify';
import TwoFactorSetupPage from './pages/TwoFactorSetup';
import StationScreen from './pages/StationScreen';
import DashboardPage from './pages/Dashboard';
import WashPerformancePage from './pages/WashPerformance';
import AdminMasterDataPage from './pages/AdminMasterData';
import ProcessingReportPage from './pages/ProcessingReport';
import WsaReportPage from './pages/WsaReport';
import TwaReportPage from './pages/TwaReport';
import DisruptionsReportPage from './pages/DisruptionsReport';
import AuditLogPage from './pages/AuditLog';
import DisruptionsListPage from './pages/DisruptionsList';
import DisruptionDetailPage from './pages/DisruptionDetail';
// PR-8a: Stammdaten-Seiten
import CustomersPage from './pages/admin/CustomersPage';
import CustomerDetailPage from './pages/admin/CustomerDetailPage';
import CustomerGroupsPage from './pages/admin/CustomerGroupsPage';
import CustomerGroupDetailPage from './pages/admin/CustomerGroupDetailPage';
import MachinesPage from './pages/admin/MachinesPage';
import MachineDetailPage from './pages/admin/MachineDetailPage';
import MachineTypesPage from './pages/admin/MachineTypesPage';
import MachineTypeDetailPage from './pages/admin/MachineTypeDetailPage';
import ProgramsPage from './pages/admin/ProgramsPage';
import ProgramDetailPage from './pages/admin/ProgramDetailPage';
import ProgramTypesPage from './pages/admin/ProgramTypesPage';
import ProgramTypeDetailPage from './pages/admin/ProgramTypeDetailPage';
import DisruptionCategoriesPage from './pages/admin/DisruptionCategoriesPage';
import DisruptionCategoryDetailPage from './pages/admin/DisruptionCategoryDetailPage';
import LinesPage from './pages/admin/LinesPage';
import LineDetailPage from './pages/admin/LineDetailPage';
import SubLineDetailPage from './pages/admin/SubLineDetailPage';
// Schichten (vereinte Seite) + Detail-Seiten (Listen-Seiten redirecten auf /admin/shifts)
import ShiftsPage from './pages/admin/ShiftsPage';
import ShiftTypeDetailPage from './pages/admin/ShiftTypeDetailPage';
import ShiftRuleDetailPage from './pages/admin/ShiftRuleDetailPage';
import StationsPage from './pages/admin/StationsPage';
import StationDetailPage from './pages/admin/StationDetailPage';
import IngestDevicesPage from './pages/admin/IngestDevicesPage';
import UsersPage from './pages/admin/UsersPage';
import UserDetailPage from './pages/admin/UserDetailPage';
// PR-8d: Tech-Seiten
import TechHomePage from './pages/tech/TechHomePage';
import TechDisruptionsPage from './pages/tech/TechDisruptionsPage';
// Phase 3B: Mein Profil
import ProfilePage from './pages/ProfilePage';
import { getMe, MeResponse } from './api/client';
// PR-5X-C: Modul-System
import { ModulesProvider } from './hooks/useModules';
// PR-5X-E: System-Seiten
import ModuleFlagsPage from './pages/admin/ModuleFlagsPage';
// Wäscheklasse-Konfiguration
import CaptureConfigPage from './pages/admin/CaptureConfigPage';
import CaptureConfigDetailPage from './pages/admin/CaptureConfigDetailPage';
import LaundryClassesPage from './pages/admin/LaundryClassesPage';
import LaundryClassDetailPage from './pages/admin/LaundryClassDetailPage';
import ArticleGroupsPage from './pages/admin/ArticleGroupsPage';
import ArticleGroupDetailPage from './pages/admin/ArticleGroupDetailPage';
// Demo: Flow-Builder Prototyp
import CaptureFlowDemoPage from './pages/admin/CaptureFlowDemoPage';
// Demo 2: Erfassungs-Flow Builder (vertikale Kette)
import CaptureFlowBuilderDemo2Page from './pages/demo/CaptureFlowBuilderDemo2Page';
// Demo 3: Erfassungs-Flow Builder (Baum-Konzept)
import CaptureFlowBuilderDemo3Page from './pages/demo/CaptureFlowBuilderDemo3Page';
import CaptureFlowBuilderDemo4Page from './pages/demo/CaptureFlowBuilderDemo4Page';
import CaptureFlowBuilderDemo5Page from './pages/demo/CaptureFlowBuilderDemo5Page';
// Station Pairing
import PairPage from './pages/PairPage';
import AdminPairPage from './pages/admin/AdminPairPage';
// Password Reset + Invite + Email Verify
import ForgotPasswordPage from './pages/ForgotPassword';
import SetPasswordPage from './pages/SetPassword';
import VerifyEmailPage from './pages/VerifyEmail';
import FeedbackPage from './pages/admin/FeedbackPage';
import ChangelogPage from './pages/admin/ChangelogPage';

// =============================================================================
// AUTH CONTEXT
// =============================================================================

interface AuthContextType {
  user: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  refresh: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

// =============================================================================
// PROTECTED ROUTE (mit returnTo)
// =============================================================================

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user || user.stationOnly) {
    // returnTo = aktuelle URL (path + query)
    const returnTo = location.pathname + location.search;
    const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    return <Navigate to={loginUrl} replace />;
  }

  return <>{children}</>;
}

// =============================================================================
// APP
// =============================================================================

export default function App() {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const data = await getMe();
      // Station-only Mode: Für UI-Zwecke als "kein User" behandeln
      // aber stationId bleibt verfügbar
      if (data.stationOnly) {
        console.info('[auth] Station-only mode, stationId:', data.stationId);
        setUser(data);
      } else {
        setUser(data);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    debugCollector.init();
    refresh();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, refresh }}>
      <ModulesProvider>
        <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/reset-password" element={<SetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/2fa/verify" element={<TwoFactorVerifyPage />} />
        <Route path="/2fa/setup" element={<TwoFactorSetupPage />} />
        <Route path="/pair" element={<PairPage />} />

        {/* Station Routes (Station Cookie OR User Session) */}
        <Route path="/station" element={<StationScreen />} />
        <Route path="/wsa" element={<StationScreen />} />
        <Route path="/vorarbeiter" element={<StationScreen />} />
        <Route path="/fl" element={<StationScreen />} />

        {/* Protected Routes */}
        <Route
          path="/admin"
          element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
        />
        <Route
          path="/dashboard"
          element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
        />
        <Route
          path="/reports/wash-performance"
          element={<ProtectedRoute><WashPerformancePage /></ProtectedRoute>}
        />
        <Route
          path="/admin/master-data"
          element={<ProtectedRoute><AdminMasterDataPage /></ProtectedRoute>}
        />
        <Route
          path="/reports/processing"
          element={<ProtectedRoute><ProcessingReportPage /></ProtectedRoute>}
        />
        <Route
          path="/reports/wsa"
          element={<ProtectedRoute><WsaReportPage /></ProtectedRoute>}
        />
        <Route
          path="/reports/twa"
          element={<ProtectedRoute><TwaReportPage /></ProtectedRoute>}
        />
        <Route
          path="/reports/disruptions"
          element={<ProtectedRoute><DisruptionsReportPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/audit"
          element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/disruptions"
          element={<ProtectedRoute><DisruptionsListPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/disruptions/:id"
          element={<ProtectedRoute><DisruptionDetailPage /></ProtectedRoute>}
        />

        {/* PR-8a: Stammdaten Routes */}
        <Route
          path="/admin/customers"
          element={<ProtectedRoute><CustomersPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/customers/:id"
          element={<ProtectedRoute><CustomerDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/customer-groups"
          element={<ProtectedRoute><CustomerGroupsPage mode="customer-groups" /></ProtectedRoute>}
        />
        <Route
          path="/admin/customer-groups/:id"
          element={<ProtectedRoute><CustomerGroupDetailPage mode="customer-groups" /></ProtectedRoute>}
        />
        <Route
          path="/admin/system-buckets"
          element={<ProtectedRoute><CustomerGroupsPage mode="system-buckets" /></ProtectedRoute>}
        />
        <Route
          path="/admin/system-buckets/:id"
          element={<ProtectedRoute><CustomerGroupDetailPage mode="system-buckets" /></ProtectedRoute>}
        />
        <Route
          path="/admin/machines"
          element={<ProtectedRoute><MachinesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/machines/:id"
          element={<ProtectedRoute><MachineDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/machine-types"
          element={<ProtectedRoute><MachineTypesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/machine-types/:id"
          element={<ProtectedRoute><MachineTypeDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/programs"
          element={<ProtectedRoute><ProgramsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/programs/:id"
          element={<ProtectedRoute><ProgramDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/program-types"
          element={<ProtectedRoute><ProgramTypesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/program-types/:id"
          element={<ProtectedRoute><ProgramTypeDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/laundry-classes"
          element={<ProtectedRoute><LaundryClassesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/laundry-classes/:id"
          element={<ProtectedRoute><LaundryClassDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/article-groups"
          element={<ProtectedRoute><ArticleGroupsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/article-groups/:id"
          element={<ProtectedRoute><ArticleGroupDetailPage /></ProtectedRoute>}
        />
        {/* Demo: Flow-Builder Prototyp */}
        <Route
          path="/admin/capture-flow-demo"
          element={<ProtectedRoute><CaptureFlowDemoPage /></ProtectedRoute>}
        />
        {/* Demo 2: Erfassungs-Flow Builder (vertikale Kette) */}
        <Route
          path="/demo/capture-flow-builder"
          element={<ProtectedRoute><CaptureFlowBuilderDemo2Page /></ProtectedRoute>}
        />
        {/* Demo 3: Erfassungs-Flow Builder (Baum-Konzept) */}
        <Route
          path="/demo/capture-flow-builder-tree"
          element={<ProtectedRoute><CaptureFlowBuilderDemo3Page /></ProtectedRoute>}
        />
        {/* Demo 4: Erfassungs-Flow Builder (Folien-Muster, abgestimmter Stand 11.08.) */}
        <Route
          path="/demo/capture-flow-builder-slides"
          element={<ProtectedRoute><CaptureFlowBuilderDemo4Page /></ProtectedRoute>}
        />
        {/* Demo 5: Erfassungs-Flow Builder (Wegekarte, Leitstand-Entwurf) */}
        <Route
          path="/demo/capture-flow-builder-wegekarte"
          element={<ProtectedRoute><CaptureFlowBuilderDemo5Page /></ProtectedRoute>}
        />
        <Route
          path="/admin/disruption-categories"
          element={<ProtectedRoute><DisruptionCategoriesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/disruption-categories/:id"
          element={<ProtectedRoute><DisruptionCategoryDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/lines"
          element={<ProtectedRoute><LinesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/lines/:id"
          element={<ProtectedRoute><LineDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/sublines/:id"
          element={<ProtectedRoute><SubLineDetailPage /></ProtectedRoute>}
        />
        {/* Schichten (vereinte Seite) + Detail-Seiten */}
        <Route
          path="/admin/shifts"
          element={<ProtectedRoute><ShiftsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/shift-types"
          element={<Navigate to="/admin/shifts?focus=types" replace />}
        />
        <Route
          path="/admin/shift-types/:id"
          element={<ProtectedRoute><ShiftTypeDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/shift-rules"
          element={<Navigate to="/admin/shifts?focus=rules" replace />}
        />
        <Route
          path="/admin/shift-rules/:id"
          element={<ProtectedRoute><ShiftRuleDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/stations"
          element={<ProtectedRoute><StationsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/stations/:id"
          element={<ProtectedRoute><StationDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/pair"
          element={<ProtectedRoute><AdminPairPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/ingest-devices"
          element={<ProtectedRoute><IngestDevicesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/users"
          element={<ProtectedRoute><UsersPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/users/:id"
          element={<ProtectedRoute><UserDetailPage /></ProtectedRoute>}
        />

        {/* Feedback System */}
        <Route
          path="/admin/feedback"
          element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/changelog"
          element={<ProtectedRoute><ChangelogPage /></ProtectedRoute>}
        />

        {/* PR-5X-E: System Routes */}
        <Route
          path="/admin/system/module-flags"
          element={<ProtectedRoute><ModuleFlagsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/capture-config"
          element={<ProtectedRoute><CaptureConfigPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/capture-config/:id"
          element={<ProtectedRoute><CaptureConfigDetailPage /></ProtectedRoute>}
        />

        {/* Phase 3B: Mein Profil */}
        <Route
          path="/profile"
          element={<ProtectedRoute><ProfilePage /></ProtectedRoute>}
        />

        {/* PR-8d: Tech Routes */}
        <Route
          path="/tech"
          element={<ProtectedRoute><TechHomePage /></ProtectedRoute>}
        />
        <Route
          path="/tech/disruptions"
          element={<ProtectedRoute><TechDisruptionsPage /></ProtectedRoute>}
        />
        <Route
          path="/tech/disruptions/:id"
          element={<ProtectedRoute><DisruptionDetailPage /></ProtectedRoute>}
        />

        {/* Default */}
        <Route path="/" element={<Navigate to={user && !user.stationOnly ? '/dashboard' : '/login'} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {user && !user.stationOnly && <FeedbackDrawer />}
      </ModulesProvider>
    </AuthContext.Provider>
  );
}
